import React, { useState, useEffect, useCallback } from 'react';

import { Select } from '@shared/ui';
import { Modal, Form, Space, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

interface BrandOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface CategoryOption {
  value: string;
  label: string;
  [key: string]: unknown;
}

interface BatchEditService {
  getBrands?: () => Promise<unknown>;
  getCategories?: () => Promise<unknown>;
  batchUpdate?: (ids: string[], updates: Record<string, unknown>) => Promise<{ success?: boolean; data?: { job_id?: string } }>;
  [key: string]: unknown;
}

interface ProductBatchEditPanelProps {
  showPanel?: boolean;
  onClosePanel: () => void;
  selected: Set<string>;
  setLastModified: (ts: number) => void;
  role?: string;
  service: BatchEditService;
  addJob?: (jobId: string, jobName: string, onComplete?: (data: unknown) => void) => void;
}

type EditType = 'brand_id' | 'product_category_id' | 'activated' | null;

const ProductBatchEditPanel: React.FC<ProductBatchEditPanelProps> = ({
  showPanel = false,
  onClosePanel,
  selected,
  setLastModified,
  role,
  service,
  addJob,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editType, setEditType] = useState<EditType>(null);
  const [editValue, setEditValue] = useState<unknown>(null);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);

  // 載入品牌與分類列表
  useEffect(() => {
    if (showPanel) {
      setLoading(true);

      const handleSuccess = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
        const list = Array.isArray(data) ? data : ((data as { data?: T[] })?.data || []);
        setter(list as T[]);
      };

      const promises: Promise<unknown>[] = [];
      if (service && typeof service.getBrands === 'function') {
        promises.push(service.getBrands().then(handleSuccess<BrandOption>(setBrands)));
      }
      if (service && typeof service.getCategories === 'function') {
        promises.push(service.getCategories().then(handleSuccess<CategoryOption>(setCategories)));
      }

      Promise.all(promises).finally(() => setLoading(false));
    }
  }, [showPanel, service]);

  const onCancel = () => {
    onClosePanel();
    form.resetFields();
    setEditType(null);
    setEditValue(null);
  };

  // 穩定的 job 完成處理函數（使用 useCallback 避免閉包問題）
  const handleJobComplete = useCallback((_data: unknown) => {
    if (setLastModified && typeof setLastModified === 'function') {
      setLastModified(Date.now());
    }
  }, [setLastModified]);

  // editType → i18n key 映射（product_category_id 在 i18n 中用 category）
  const fieldLabelKey = editType === 'product_category_id' ? 'category' : editType;

  const onSubmit = async () => {
    if (!editType || editValue === null) {
      return;
    }

    // 判斷是清空還是設定值
    const isClear = editValue === '__CLEAR__';
    const actionValue = isClear ? null : editValue;
    const actionLabel = isClear
      ? t('shared.products.batch_edit.clear_value')
      : editType === 'brand_id'
          ? brands.find((b) => b.id === editValue)?.name
          : editType === 'product_category_id'
            ? categories.find((c) => c.value === editValue)?.label
            : editValue;

    Modal.confirm({
      title: t('shared.products.batch_edit.confirm_title', { count: selected.size }) as string,
      content: t(
        isClear ? 'shared.products.batch_edit.confirm_clear_message' : 'shared.products.batch_edit.confirm_set_message',
        {
          field: t(`shared.products.batch_edit.fields.${fieldLabelKey}`),
          value: actionLabel,
          count: selected.size
        }
      ) as string,
      onOk: async () => {
        try {
          let result: { success?: boolean; data?: { job_id?: string } } | undefined;

          if (service && typeof service.batchUpdate === 'function') {
            // Service Mode
            result = await service.batchUpdate(Array.from(selected), {
              [editType]: actionValue
            });
          }

          if (result && result.success && result.data?.job_id) {
            // 添加背景任務到通知管理器
            if (addJob && typeof addJob === 'function') {
              const jobName = t('shared.products.batch_edit.job_name', { count: selected.size }) as string;
              addJob(result.data.job_id, jobName, handleJobComplete);
            } else {
              // Fallback: 直接重新載入
              setLastModified(Date.now());
            }
          }

          onCancel();
        } catch (error) {
          console.error('[ProductBatchEditPanel] Error:', error);
        }
      }
    });
  };

  const brandOptions = [
    { label: t('shared.products.batch_edit.clear_option'), value: '__CLEAR__' },
    ...brands.map((brand) => ({
      label: brand.name,
      value: brand.id,
    }))
  ];

  const categoryOptions = [
    { label: t('shared.products.batch_edit.clear_option'), value: '__CLEAR__' },
    ...categories,
  ];

  return (
    <Modal
      title={t('shared.products.batch_edit.title') as string}
      open={showPanel}
      onCancel={onCancel}
      width={600}
      footer={
        <Space>
          <Button onClick={onCancel}>
            {t('common.button.cancel') as string}
          </Button>
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={!editType || editValue === null}
          >
            {t('common.button.confirm') as string}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Alert
          message={t('shared.products.batch_edit.selected_count', { count: selected.size }) as string}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          label={t('shared.products.batch_edit.edit_field_label') as string}
        >
          <Select
            options={[
              // Brand 是 merchant-scoped，operator 跨 merchant 批次編輯會造成錯誤指派
              ...(role !== 'operator' ? [{ value: 'brand_id', label: t('shared.products.batch_edit.fields.brand_id') as string }] : []),
              { value: 'product_category_id', label: t('shared.products.batch_edit.fields.category') as string },
              { value: 'activated', label: t('shared.products.batch_edit.fields.activated') as string }
            ]}
            onChange={(option: { value: EditType } | null) => {
              setEditType(option ? option.value : null);
              setEditValue(null);
              form.setFieldsValue({ editValue: null });
            }}
            placeholder={t('common.select.please_select') as string}
            value={editType ? { value: editType, label: t(`shared.products.batch_edit.fields.${fieldLabelKey}`) as string } : null}
          />
        </Form.Item>

        {editType === 'brand_id' && (
          <Form.Item
            label={t('shared.products.batch_edit.select_brand_label') as string}
            name="editValue"
          >
            <Select
              options={brandOptions}
              onChange={(option: { value: unknown } | null) => setEditValue(option ? option.value : null)}
              placeholder={t('common.select.please_select') as string}
              loading={loading}
            />
          </Form.Item>
        )}

        {editType === 'product_category_id' && (
          <Form.Item
            label={t('shared.products.batch_edit.input_category_label') as string}
            name="editValue"
          >
            <Select
              options={categoryOptions}
              onChange={(option: { value: unknown } | null) => setEditValue(option ? option.value : null)}
              placeholder={t('common.select.please_select') as string}
              loading={loading}
            />
          </Form.Item>
        )}

        {editType === 'activated' && (
          <Form.Item
            label={t('shared.products.batch_edit.select_activated_label') as string}
            name="editValue"
          >
            <Select
              options={[
                { value: true, label: t('shared.products.batch_edit.activated_options.true') as string },
                { value: false, label: t('shared.products.batch_edit.activated_options.false') as string }
              ]}
              onChange={(option: { value: unknown } | null) => setEditValue(option ? option.value : null)}
              placeholder={t('common.select.please_select') as string}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ProductBatchEditPanel;
